import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, ChevronDown, Table as TableIcon, Loader2, AlertCircle, X } from 'lucide-react';
import axios from '../axiosInstance';
import * as XLSX from 'xlsx';

/* ───────────── helpers ───────────── */


const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '0.00';
  return parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ───────────── component ───────────── */
export default function IncidentalReports() {

  /* master data for filters */
  const [seasons, setSeasons] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);

  /* filters */
  const [filters, setFilters] = useState({
    season_id: '',
    commodity_id: '',
    state_id: '',
    branch_id: '',
  });

  /* report data */
  const [reportData, setReportData] = useState([]);
  const [totalProcurementQty, setTotalProcurementQty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasFetched, setHasFetched] = useState(false);

  /* ── load master data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const promises = [
          axios.get('seasons?paginate=false'),
          axios.get('commodity'),
          axios.get('states'),
          axios.get('user/branches?paginate=false'),
        ];
        const results = await Promise.all(promises);
        setSeasons(results[0].data?.seasons || results[0].data?.data || (Array.isArray(results[0].data) ? results[0].data : []));
        setCommodities(results[1].data?.commodities || results[1].data?.data || (Array.isArray(results[1].data) ? results[1].data : []));
        setStates(results[2].data?.states || results[2].data?.data || (Array.isArray(results[2].data) ? results[2].data : []));
        setBranches(results[3].data?.data || results[3].data?.branches || (Array.isArray(results[3].data) ? results[3].data : []));
      } catch (err) {
        console.error('Master data load error:', err);
        setError('Failed to load filter data. Please try again.');
      }
    };
    load();
  }, []);

  /* ── derived filter labels ── */
  const selectedSeason = useMemo(() => seasons.find(s => s.id === filters.season_id), [seasons, filters.season_id]);
  const selectedCommodity = useMemo(() => commodities.find(c => c.id === filters.commodity_id), [commodities, filters.commodity_id]);
  const selectedState = useMemo(() => states.find(s => s.id === filters.state_id), [states, filters.state_id]);
  const selectedBranch = useMemo(() => branches.find(b => b.id === filters.branch_id), [branches, filters.branch_id]);

  const allFiltersSelected = filters.season_id && filters.commodity_id && filters.state_id && filters.branch_id;

  /* ── fetch report data ── */
  const fetchReport = useCallback(async () => {
    if (!allFiltersSelected) return;
    setLoading(true);
    setError('');
    setHasFetched(false);
    try {
      const params = new URLSearchParams();
      params.append('paginate', 'false');
      if (filters.season_id) params.append('season_id', filters.season_id);
      if (filters.commodity_id) params.append('commodity_id', filters.commodity_id);
      if (filters.state_id) params.append('state_id', filters.state_id);
      if (filters.branch_id) params.append('branch_id', filters.branch_id);

      const res = await axios.get(`store-incidentals?${params.toString()}`);
      const records = res.data?.data || res.data?.storeIncidentals || (Array.isArray(res.data) ? res.data : []);

      // Aggregate items across all records, grouped by particular name
      const particularMap = {};
      let totalQty = 0;

      records.forEach(record => {
        totalQty += parseFloat(record.procurement_quantity) || 0;
        if (record.items && Array.isArray(record.items)) {
          record.items.forEach(item => {
            const particularName = item.particular?.name || 'Unknown';
            const particularCode = item.particular?.code || '';
            const key = particularName;

            if (!particularMap[key]) {
              particularMap[key] = {
                particulars: particularName,
                code: particularCode,
                incidentalRate: 0,
                approvalAmount: 0,
                claimedAmount: 0,
                paidAmount: 0,
                pendingAmount: 0,
                penalty: 0,
                remark: '',
                count: 0,
              };
            }

            const entry = particularMap[key];
            entry.incidentalRate += parseFloat(item.incidental_rate) || 0;
            entry.approvalAmount += parseFloat(item.approval_amount) || 0;
            entry.claimedAmount += parseFloat(item.incidental_amount_claimed) || 0;
            entry.paidAmount += parseFloat(item.incidental_amount_paid) || 0;
            entry.pendingAmount += parseFloat(item.incidental_amount_pending) || 0;
            entry.penalty += parseFloat(item.penalty) || 0;
            entry.remark = item.remark || entry.remark;
            entry.count += 1;
          });
        }
      });

      // Average out the rate per particular (since rate is per unit, not summed)
      Object.values(particularMap).forEach(entry => {
        if (entry.count > 0) {
          entry.incidentalRate = entry.incidentalRate / entry.count;
        }
      });

      setReportData(Object.values(particularMap));
      setTotalProcurementQty(totalQty);
      setHasFetched(true);
    } catch (err) {
      console.error('Report fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data.');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [allFiltersSelected, filters]);

  /* Auto-fetch when all filters are selected */
  useEffect(() => {
    if (allFiltersSelected) {
      fetchReport();
    } else {
      setReportData([]);
      setHasFetched(false);
    }
  }, [filters.season_id, filters.commodity_id, filters.state_id, filters.branch_id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── computed totals ── */
  const totals = useMemo(() => {
    return reportData.reduce(
      (acc, curr) => {
        acc.incidentalRate += curr.incidentalRate;
        acc.approvalAmount += curr.approvalAmount;
        acc.claimedAmount += curr.claimedAmount;
        acc.paidAmount += curr.paidAmount;
        acc.pendingAmount += curr.pendingAmount;
        return acc;
      },
      { incidentalRate: 0, approvalAmount: 0, claimedAmount: 0, paidAmount: 0, pendingAmount: 0 }
    );
  }, [reportData]);

  /* ── filter handler ── */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  /* ── Excel download ── */
  const handleDownloadExcel = () => {
    if (reportData.length === 0) return;

    const seasonName = selectedSeason?.name || '';
    const commodityName = selectedCommodity?.commodityname || selectedCommodity?.name || '';
    const stateName = selectedState?.name || '';
    const branchName = selectedBranch?.first_name || selectedBranch?.name || '';

    // Build rows for Excel
    const wsData = [];

    // Header rows
    wsData.push([`Season ${seasonName}`]);
    wsData.push([`Commodity- ${commodityName}`]);
    wsData.push([`State- ${stateName}`]);
    wsData.push([`Procurred Quantity by ${branchName} in qt-${totalProcurementQty}`]);
    wsData.push([]); // blank row

    // Column headers
    wsData.push([
      'Particulars',
      'Incidental rate as approved by the DA &FW in qt.',
      'Amount as per the Approval DA &FW',
      'Incidental Amount Claimed by  BO',
      'Incidental Amount Paid by HO',
      'Incidental Amount Pending',
      'Remark',
    ]);

    // Data rows
    reportData.forEach(item => {
      wsData.push([
        item.particulars,
        parseFloat(item.incidentalRate.toFixed(2)),
        parseFloat(item.approvalAmount.toFixed(2)),
        parseFloat(item.claimedAmount.toFixed(2)),
        parseFloat(item.paidAmount.toFixed(2)),
        parseFloat(item.pendingAmount.toFixed(2)),
        item.remark || '',
      ]);
    });

    // Total row
    wsData.push([
      'Total',
      parseFloat(totals.incidentalRate.toFixed(2)),
      parseFloat(totals.approvalAmount.toFixed(2)),
      parseFloat(totals.claimedAmount.toFixed(2)),
      parseFloat(totals.paidAmount.toFixed(2)),
      parseFloat(totals.pendingAmount.toFixed(2)),
      '',
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Particulars
      { wch: 25 }, // Rate
      { wch: 25 }, // Approval Amount
      { wch: 25 }, // Claimed
      { wch: 25 }, // Paid
      { wch: 20 }, // Pending
      { wch: 20 }, // Remark
    ];

    // Merge header rows across all columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Incidental Report');
    XLSX.writeFile(wb, `Incidental_Report_${seasonName}_${stateName}_${branchName}.xlsx`);
  };

  /* ── render ── */
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="w-full">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4"
        >
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Incidental Management</h5>
            <h1 className="text-4xl font-black text-blue-800 tracking-tight">Reports</h1>
            <p className="text-slate-500 mt-2 max-w-xl text-sm md:text-base">
              Select filters to generate an incidental report. Download as Excel for offline use.
            </p>
          </div>
          {reportData.length > 0 && (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleDownloadExcel}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-emerald-100 transition-all"
            >
              <Download size={20} />
              Download Excel
            </motion.button>
          )}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-8"
        >
          <h2 className="text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
            <TableIcon size={20} className="text-blue-600" /> Filter Criteria
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Season */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Season <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="season_id"
                  value={filters.season_id}
                  onChange={handleFilterChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Season</option>
                  {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Commodity */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Commodity <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="commodity_id"
                  value={filters.commodity_id}
                  onChange={handleFilterChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Commodity</option>
                  {commodities.map(c => <option key={c.id} value={c.id}>{c.commodityname || c.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* State */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">State <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="state_id"
                  value={filters.state_id}
                  onChange={handleFilterChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Branch */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Branch <span className="text-red-500">*</span></label>
              <div className="relative">
                <select
                  name="branch_id"
                  value={filters.branch_id}
                  onChange={handleFilterChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.first_name || b.name}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold"
            >
              <AlertCircle size={20} /> {error}
              <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded-lg transition">
                <X size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Report content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20 text-blue-600"
            >
              <Loader2 size={40} className="animate-spin mb-4" />
              <span className="font-bold text-sm">Generating report...</span>
            </motion.div>
          ) : allFiltersSelected && hasFetched ? (
            <motion.div
              key="report-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Info Bars - matching Excel header format */}
              <div className="space-y-0 mb-1 shadow-sm rounded-2xl overflow-hidden">
                <div className="bg-[#EBF2FF] py-3 text-center border-b border-blue-100">
                  <p className="text-sm font-black text-blue-900 uppercase tracking-widest">
                    Season {selectedSeason?.name || ''}
                  </p>
                </div>
                <div className="bg-[#EBF2FF] py-3 text-center border-b border-blue-100">
                  <p className="text-sm font-black text-blue-900 uppercase tracking-widest">
                    Commodity- {selectedCommodity?.commodityname || selectedCommodity?.name || ''}
                  </p>
                </div>
                <div className="bg-[#EBF2FF] py-3 text-center border-b border-blue-100">
                  <p className="text-sm font-black text-blue-900 uppercase tracking-widest">
                    State- {selectedState?.name || ''}
                  </p>
                </div>
                <div className="bg-[#EBF2FF] py-4 text-center">
                  <p className="text-sm font-black text-blue-900 uppercase tracking-widest">
                    Procurred Quantity by {selectedBranch?.first_name || selectedBranch?.name || 'BO'} in qt-{totalProcurementQty.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Data Table */}
              {reportData.length > 0 ? (
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden mb-20">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1200px]">
                      <thead className="bg-[#F1F5F9]/80 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200">
                            Particulars
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200 max-w-[200px]">
                            Incidental Rate as Approved by the DA & FW in QT.
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200">
                            Amount as per the Approval DA & FW
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200">
                            Incidental Amount Claimed by BO
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200">
                            Incidental Amount Paid by HO
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center border-r border-slate-200">
                            Incidental Amount Pending
                          </th>
                          <th className="px-6 py-5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                            Remark
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {reportData.map((item, idx) => (
                          <motion.tr
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            className="hover:bg-slate-50/50 transition-colors"
                          >
                            <td className="px-6 py-4 text-xs font-bold text-slate-800 bg-[#FFF7AD] border-r border-slate-200 text-center">
                              {item.particulars}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center border-r border-slate-200">
                              {item.incidentalRate.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center border-r border-slate-200">
                              {formatCurrency(item.approvalAmount)}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center border-r border-slate-200">
                              {formatCurrency(item.claimedAmount)}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center border-r border-slate-200">
                              {formatCurrency(item.paidAmount)}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center border-r border-slate-200">
                              {formatCurrency(item.pendingAmount)}
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600 text-center">
                              {item.remark || '-'}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[#F8FAFC] border-t-2 border-slate-200">
                        <tr className="font-black text-slate-900 bg-slate-50">
                          <td className="px-6 py-5 text-center bg-[#FFF7AD] border-r border-slate-200 text-xs">Total</td>
                          <td className="px-6 py-5 text-center text-xs border-r border-slate-200">{totals.incidentalRate.toFixed(2)}</td>
                          <td className="px-6 py-5 text-center text-xs border-r border-slate-200">{formatCurrency(totals.approvalAmount)}</td>
                          <td className="px-6 py-5 text-center text-xs border-r border-slate-200">{formatCurrency(totals.claimedAmount)}</td>
                          <td className="px-6 py-5 text-center text-xs border-r border-slate-200">{formatCurrency(totals.paidAmount)}</td>
                          <td className="px-6 py-5 text-center text-xs border-r border-slate-200">{formatCurrency(totals.pendingAmount)}</td>
                          <td className="px-6 py-5 text-center text-xs"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-16 text-center mt-2">
                  <div className="max-w-md mx-auto space-y-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600">
                      <TableIcon size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-700">No Data Found</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      No incidental records found for the selected filters. Try adjusting your filter criteria.
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-blue-50/30 border-2 border-dashed border-blue-200 rounded-3xl p-16 text-center"
            >
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-600 shadow-inner">
                  <TableIcon size={32} />
                </div>
                <h3 className="text-xl font-bold text-blue-900">Select Filter Criteria</h3>
                <p className="text-blue-700/60 leading-relaxed text-sm">
                  To view the reports, please select a <strong>Season</strong>, <strong>Commodity</strong>, <strong>State</strong>, and <strong>Branch</strong> from the filters above.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
